import React, { useState, useEffect } from "react";
import { Client, Databases, ID } from "appwrite";
import image from "../assets/Academy/enroll.webp";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import close from "../assets/Academy/close.webp";
import { 
  parsePhoneNumberFromString, 
  isValidPhoneNumber 
} from "libphonenumber-js";
import StripeCheckout from "./StripeCheckout"; // Import the stripe integration
import config from "../config/config";

const Enroll = ({ isOpen, onClose, utmContent, academyTitle, courseType }) => {
  const [firstname, setFirstName] = useState("");
  const [lastname, setLastName] = useState("");
  const [businessemail, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [jobtitle, setJobTitle] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSuccessOpen, setIsSuccessOpen] = useState(false);
  const [paymentError, setPaymentError] = useState(null);

  const [utmParams, setUtmParams] = useState({
    utm_source: "",
    utm_medium: "",
    utm_campaign: "",
    utm_id: "",
    utm_term: "",
    utm_content: "",
  });

  // Appwrite client setup
  const client = new Client()
    .setEndpoint(process.env.REACT_APP_ENDPOINT)
    .setProject(process.env.REACT_APP_PROJECT_ID);

  const databases = new Databases(client);

  // UTM Parameters Capture
  useEffect(() => {
    const captureUtmParams = () => {
      const params = new URLSearchParams(window.location.search);
      const utmFields = [
        "utm_source",
        "utm_medium",
        "utm_campaign",
        "utm_id",
        "utm_term",
      ];

      const newUtmParams = {
        utm_content: utmContent,
      };
      utmFields.forEach((field) => {
        newUtmParams[field] = params.get(field) || "";
      });
      
      setUtmParams(newUtmParams);
    };

    captureUtmParams();
    window.addEventListener("popstate", captureUtmParams);

    return () => {
      window.removeEventListener("popstate", captureUtmParams);
    };
  }, [utmContent]);

  // Phone Number Validation
  const handlePhoneNumberChange = (number, country) => {
    setPhoneNumber(number);

    try {
      const fullNumber = number.startsWith("+") ? number : `+${number}`;

      if (isValidPhoneNumber(fullNumber)) {
        const phoneNumberObj = parsePhoneNumberFromString(fullNumber);
        const nationalNumber = phoneNumberObj.nationalNumber;

        const expectedLength = phoneNumberObj.nationalNumber?.length;
        const actualLength = nationalNumber?.length;

        if (actualLength < expectedLength) {
          setErrors((prev) => ({
            ...prev,
            phoneNumber: `Phone number is too short - needs ${expectedLength} digits`,
          }));
        } else if (actualLength > expectedLength) {
          setErrors((prev) => ({
            ...prev,
            phoneNumber: `Phone number is too long - should be ${expectedLength} digits`,
          }));
        } else {
          setErrors((prev) => ({ ...prev, phoneNumber: "" }));
        }
      } else {
        setErrors((prev) => ({
          ...prev,
          phoneNumber: "Please enter a valid phone number",
        }));
      }
    } catch (error) {
      console.error("Phone validation error:", error);
      setErrors((prev) => ({
        ...prev,
        phoneNumber: "Please enter a valid phone number",
      }));
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsLoading(true);
    setPaymentError(null);

    try {
      if (!phoneNumber) {
        setErrors({ phoneNumber: "Phone number is required" });
        setIsLoading(false);
        return;
      }

      const fullNumber = phoneNumber.startsWith("+") 
        ? phoneNumber 
        : `+${phoneNumber}`;

      if (!isValidPhoneNumber(fullNumber)) {
        setErrors((prev) => ({
          ...prev,
          phoneNumber: "Please enter a valid phone number",
        }));
        setIsLoading(false);
        return;
      }

      // Prepare form data for Appwrite - DON'T include academyTitle
      const appwriteFormData = {
        firstname,
        lastname,
        businessemail,
        phoneNumber,
        jobtitle,
        companyName,
        academyTitle,
        courseType,  // Add academy title to Appwrite
        utmSource: utmParams.utm_source,
        utmMedium: utmParams.utm_medium,
        utmCampaign: utmParams.utm_campaign,
        utmId: utmParams.utm_id,
        utmTerm: utmParams.utm_term,
        utmContent: utmParams.utm_content,
      };

      // Save to Appwrite database first
      await databases.createDocument(
        process.env.REACT_APP_DATABASE_ID,
        config.collectionIds.academy,
        ID.unique(),
        appwriteFormData
      );

      // For Stripe, we can include the academyTitle
      const stripeFormData = {
        ...appwriteFormData,
        academyTitle: academyTitle // Add academy title just for Stripe, not for Appwrite
      };

      // Redirect to Stripe checkout with complete data
      await StripeCheckout.createCheckoutSession(stripeFormData, courseType);
      
      // Note: The page will be redirected to Stripe, so the code below may not execute
      // It's a fallback in case the redirect doesn't happen immediately
      setIsLoading(false);
      
    } catch (error) {
      console.error("Error during form submission or payment:", error);
      setPaymentError("There was an error processing your request. Please try again.");
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setFirstName("");
    setLastName("");
    setEmail("");
    setPhoneNumber("");
    setJobTitle("");
    setCompanyName("");
    setErrors({});
    setPaymentError(null);
  };

  const closeSuccessModal = () => {
    setIsSuccessOpen(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
        <div className="bg-white rounded-lg shadow-lg p- w-11/12 md:w-full max-w-4xl relative flex">
          <button onClick={onClose} className="absolute top-4 right-4">
            <img src={close} alt="Close" className="w-3 h-3" />
          </button>

          <div className="hidden md:block w-3/2 p-2 flex justify-center items-center">
            <img src={image} alt="Enroll" className="w-full h-full rounded-lg" />
          </div>
   
          <div className="md:w-4/5 p-5">
            <h2 className="text-lg font-semibold mb-4">
              Please fill the form to proceed to payment 
            </h2>
            <form onSubmit={handleSubmit} className="space-y-2">
              {paymentError && (
                <div className="bg-red-100 text-red-700 p-2 rounded mb-4">
                  {paymentError}
                </div>
              )}
            
              <div className="mb-0">
                <label className=" text-gray-700">First Name</label>
                <input
                  id="firstname"
                  type="text"
                  name="firstname"
                  value={firstname}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="w-full px-3 py-2 border rounded"
                  required
                />
                {errors.firstname && (
                  <p className="text-red-500 text-sm">{errors.firstname}</p>
                )}
              </div>
              <div className="mb-0">
                <label className=" text-gray-700">Last Name</label>
                <input
                  type="text"
                  name="lastname"
                  id="lastname3"
                  value={lastname}
                  onChange={(e) => setLastName(e.target.value)}
                  className="w-full px-3 py-2 border rounded"
                  required
                />
                {errors.lastname && (
                  <p className="text-red-500 text-sm">{errors.lastname}</p>
                )}
              </div>
        
              <div className="mb-0">
                <label className=" text-gray-700"> Email</label>
                <input
                  id="businessemail"
                  type="email"
                  name="businessemail"
                  value={businessemail}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-3 py-2 border rounded"
                  required
                />
                {errors.businessemail && (
                  <p className="text-red-500 text-sm">
                    {errors.businessemail}
                  </p>
                )}
              </div>
              <div className="mb-0">
                <label id="Phonenumber" className=" text-gray-700">
                  Phone Number
                </label>
                <PhoneInput
                  country={"in"}
                  value={phoneNumber}
                  onChange={(number, country) =>
                    handlePhoneNumberChange(number, country)
                  }
                  inputClass="custom-phone-input2 "
                  buttonClass="border rounded"
                  dropdownClass="border rounded"
                  required
                  enableSearch={true}
                  inputProps={{
                    required: true,
                    autoFocus: false,
                  }}
                />
                {errors.phoneNumber && (
                  <p className="text-red-500 text-sm">{errors.phoneNumber}</p>
                )}
              </div>
             
              <div className="mb-0">
                <label className=" text-gray-700">Job Title</label>
                <input
                  type="text"
                  name="Job Title" 
                  id="Job Title" 
                  value={jobtitle}
                  onChange={(e) => setJobTitle(e.target.value)}
                  className="w-full px-3 py-2 border rounded"
                  required
                />
                {errors.jobtitle && (
                  <p className="text-red-500 text-sm">{errors.jobtitle}</p>
                )}
              </div>
              <div className="mb-4">
                <label className="text-gray-700">Company Name</label>
                <input
                  id="Companyname"
                  type="text"
                  name="companyName"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  className="w-full px-3 py-2 border rounded"
                  required
                />
                {errors.companyName && (
                  <p className="text-red-500 text-sm">{errors.companyName}</p>
                )}
              </div>
            
              <button 
                type="submit" 
                className={`w-full p-2 bg-[rgba(62,89,183,1)] text-white rounded hover:bg-blue-700 ${
                  isLoading ? "opacity-50 cursor-not-allowed" : ""
                }`}
                disabled={isLoading}
              >
                {isLoading ? "Processing..." : "Proceed to Payment"}
              </button>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default Enroll;